import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedPath } from '../utils/routes';

const API = process.env.REACT_APP_API_URL || '/api';

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const token = searchParams.get('token');
  const uid = searchParams.get('uid');

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [error, setError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token || !uid) {
      setValidating(false);
      setError(t.setPassword?.invalidLink || 'Invalid or missing link parameters.');
      return;
    }
    validateToken();
  }, [token, uid]);

  const validateToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/set-password/validate`, { params: { token, uid } });
      if (res.data.valid) {
        setValid(true);
        setFirstName(res.data.firstName || '');
      } else {
        setError(res.data.error || (t.setPassword?.expired || 'This link has expired or is invalid.'));
      }
    } catch (err) {
      setError(t.setPassword?.expired || 'This link has expired or is invalid.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 6) {
      setFormError(t.setPassword?.tooShort || 'Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t.setPassword?.mismatch || 'Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/set-password`, {
        token, uid, password, marketing_opt_in: marketingOptIn
      });
      // Store JWT and login
      localStorage.setItem('cjpa_token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      login(res.data.token, res.data.user);
      navigate(lp('dashboard'));
    } catch (err) {
      setFormError(err.response?.data?.error || (t.setPassword?.error || 'Failed to set password. The link may have expired.'));
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = {
    background: '#0C1220', minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '2rem'
  };

  const cardStyle = {
    background: '#111827', border: '1px solid rgba(148,163,184,0.2)',
    padding: 'clamp(2rem, 4vw, 3rem)', maxWidth: '460px', width: '100%'
  };

  const inputStyle = {
    width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.25)',
    color: '#E8ECF0', padding: '0.75rem', fontSize: '0.9rem',
    fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontSize: '0.7rem', color: '#94A3B8',
    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem'
  };

  if (validating) {
    return (
      <div style={containerStyle}>
        <div style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem' }}>
          {t.common?.loading || 'Loading...'}
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '1rem' }}>
            {t.setPassword?.invalidTitle || 'Invalid Link'}
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.9rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '0.5rem' }}>
          {t.setPassword?.title || 'Set Your Password'}
        </h1>
        {firstName && (
          <p style={{ color: '#94A3B8', fontSize: '0.9rem', marginBottom: '2rem' }}>
            {(t.setPassword?.welcome || 'Welcome, {name}! Create a password to access your account.').replace('{name}', firstName)}
          </p>
        )}

        {formError && (
          <div style={{
            padding: '0.75rem', marginBottom: '1.5rem',
            background: 'rgba(220,70,70,0.15)', border: '1px solid rgba(220,70,70,0.4)',
            color: '#d07070', fontSize: '0.85rem'
          }}>
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>{t.setPassword?.passwordLabel || 'Password'}</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
              minLength={6}
              required
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>{t.setPassword?.confirmLabel || 'Confirm Password'}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={inputStyle}
              minLength={6}
              required
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                style={{ marginTop: '0.2rem', accentColor: '#D42B2B' }}
              />
              <span style={{ color: '#94A3B8', fontSize: '0.8rem', lineHeight: 1.5 }}>
                {t.setPassword?.marketingLabel || "I agree to receive offers, promotions, and tips by email from Mécanique Auto CJPA. I can unsubscribe at any time."}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', background: '#D42B2B', border: 'none',
              color: '#0C1220', padding: '1rem', fontSize: '0.7rem',
              letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.5 : 1,
              fontFamily: "'Barlow', sans-serif"
            }}
          >
            {submitting ? (t.setPassword?.setting || 'Setting Password...') : (t.setPassword?.submit || 'Set Password & Sign In')}
          </button>
        </form>
      </div>
    </div>
  );
}
